import { ProLayout } from '@ant-design/pro-components';
import type { MenuDataItem } from '@ant-design/pro-components';
import {
  AlertOutlined,
  ApartmentOutlined,
  ApiOutlined,
  AppstoreOutlined,
  BankOutlined,
  BellOutlined,
  BranchesOutlined,
  BugOutlined,
  ControlOutlined,
  CustomerServiceOutlined,
  GlobalOutlined,
  MonitorOutlined,
  NotificationOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  BarcodeOutlined,
  BulbOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  CommentOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  ExperimentOutlined,
  GiftOutlined,
  HomeOutlined,
  IdcardOutlined,
  KeyOutlined,
  LaptopOutlined,
  LogoutOutlined,
  FileSearchOutlined,
  MenuOutlined,
  PictureOutlined,
  ProfileOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  StopOutlined,
  TagsOutlined,
  TeamOutlined,
  ToolOutlined,
  UnorderedListOutlined,
  UserOutlined,
  VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Spin } from 'antd';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, useMemo } from 'react';

import { adminLogout } from '@/api/auth';
import type { Menu } from '@/api/rbac';
import PageErrorBoundary from '@/components/PageErrorBoundary';
import { useAuthStore } from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { placeSystemManagementLast } from './menuOrder';

/**
 * 按 V29 的 6 大目录设计组织。SN 平台保持原扁平结构不动。
 *
 * 订阅管理下的菜单名与页面标题保持一致（套餐管理 / 设备订阅 / 订阅权益赠送），
 * 页面标题由页面自己维护，这里只保证菜单入口的名字与 path 一一对应。
 */
export const menuData: MenuDataItem[] = [
  { path: '/dashboard', name: '概览', icon: <DashboardOutlined /> },

  {
    path: '/catalog',
    name: '商品管理',
    icon: <DatabaseOutlined />,
    children: [
      { path: '/catalog/company', name: '公司管理', icon: <BankOutlined /> },
      { path: '/catalog/product-hub', name: '产品库', icon: <ShopOutlined /> },
      { path: '/catalog/capabilities', name: '能力集', icon: <ThunderboltOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/device',
    name: '设备管理',
    icon: <LaptopOutlined />,
    children: [
      { path: '/device/list', name: '设备列表', icon: <LaptopOutlined /> },
      { path: '/device/groups', name: '设备分组', icon: <ApartmentOutlined /> },
      { path: '/device/ota-tasks', name: 'OTA 任务', icon: <RocketOutlined /> },
      { path: '/device/events', name: '事件检索', icon: <FileSearchOutlined /> },
      { path: '/device/version', name: '版本管理', icon: <CloudUploadOutlined /> },
      { path: '/device/ota-baselines', name: 'OTA 基线', icon: <VerticalAlignBottomOutlined /> },
      { path: '/device/gifts', name: '礼物管理', icon: <GiftOutlined /> },
      { path: '/device/slideshow-group', name: '轮播图组', icon: <PictureOutlined /> },
      { path: '/device/calendar', name: '日历图', icon: <CalendarOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/sn',
    name: 'SN 平台',
    icon: <BarcodeOutlined />,
    children: [
      { path: '/sn/overview', name: '概览', icon: <DashboardOutlined /> },
      { path: '/sn/batch', name: 'SN 批次', icon: <TagsOutlined /> },
      { path: '/sn/list', name: 'SN 列表', icon: <UnorderedListOutlined /> },
      { path: '/sn/test-report', name: '产测报告', icon: <ExperimentOutlined /> },
      { path: '/sn/scrap-record', name: 'SN 作废记录', icon: <StopOutlined /> },
      { path: '/sn/factory', name: '工厂管理', icon: <HomeOutlined /> },
      { path: '/sn/parser', name: 'SN 解析器', icon: <ExperimentOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/subscription',
    name: '订阅管理',
    icon: <CreditCardOutlined />,
    children: [
      { path: '/subscription/plan', name: '套餐管理', icon: <DollarOutlined /> },
      { path: '/subscription/device-plan', name: '设备订阅', icon: <CreditCardOutlined /> },
      { path: '/subscription/esim-usage', name: 'eSIM 用量', icon: <GlobalOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/watch-face',
    name: '表盘管理',
    icon: <ClockCircleOutlined />,
    children: [
      { path: '/watch-face/list', name: '表盘列表', icon: <PictureOutlined /> },
      { path: '/watch-face/custom', name: '系统预设表盘', icon: <AppstoreOutlined /> },
      { path: '/watch-face/user', name: '用户上传表盘', icon: <UserOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/llm',
    name: '大模型管理',
    icon: <RobotOutlined />,
    children: [
      { path: '/llm/configs', name: '配置管理', icon: <ApiOutlined /> },
      { path: '/llm/models', name: '模型管理', icon: <AppstoreOutlined /> },
      { path: '/llm/recommend', name: '推荐模型', icon: <BulbOutlined /> },
      { path: '/llm/stats', name: '使用统计', icon: <BarChartOutlined /> },
      { path: '/llm/metering', name: '用量与估算金额', icon: <BarChartOutlined /> },
      { path: '/llm/prices', name: '模型价格', icon: <ApiOutlined /> },
      { path: '/llm/price-audit', name: '价格对账', icon: <ApiOutlined /> },
      { path: '/llm/profile', name: 'Profile 管理', icon: <ProfileOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/operation',
    name: '平台运营',
    icon: <ToolOutlined />,
    children: [
      { path: '/operation/user', name: '用户管理', icon: <TeamOutlined /> },
      { path: '/operation/feedback', name: '反馈管理', icon: <CommentOutlined /> },
      { path: '/operation/partner', name: '代理商管理', icon: <BranchesOutlined /> },
      { path: '/operation/workbench', name: '工作台搜索', icon: <FileSearchOutlined /> },
      // 后端 V160：挂平台运营目录，但 path 是 /ops/image-asset
      { path: '/ops/image-asset', name: '图片素材库', icon: <PictureOutlined /> },
      {
        path: '/operation/notifications',
        name: '通知管理',
        icon: <NotificationOutlined />,
        children: [
          { path: '/operation/notifications/templates', name: '模板管理' },
          { path: '/operation/notifications/deliveries', name: '发送记录' },
        ],
      },
      // 后端菜单：故事审核挂在「平台运营」下，path 仍为 /ai/story-review
      { path: '/ai/story-review', name: '故事库审核', icon: <ProfileOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/ops',
    name: '运维中心',
    icon: <MonitorOutlined />,
    children: [
      { path: '/ops/alert-rules', name: '告警规则', icon: <AlertOutlined /> },
      { path: '/ops/alert-events', name: '告警事件', icon: <BellOutlined /> },
      { path: '/ops/alert-channels', name: '通知通道', icon: <ApiOutlined /> },
      { path: '/ops/config', name: 'Remote Config', icon: <ControlOutlined /> },
      { path: '/ops/connectivity', name: '连接性看板', icon: <DashboardOutlined /> },
    ],
  } as MenuDataItem,

  {
    path: '/repair',
    name: '售后服务',
    icon: <CustomerServiceOutlined />,
    children: [
      { path: '/repair/tickets', name: '售后工单', icon: <ToolOutlined /> },
    ],
  } as MenuDataItem,
];

/**
 * 支付管理、开放平台和系统管理均以 /admin/rbac/me 的菜单树为准。
 * 以下静态菜单只用于该接口首次请求失败时兼容旧后端，不能用于掩盖漏执行 RBAC 迁移。
 * 我的资料走右上角头像下拉，不在左侧菜单里出现。
 */
const paymentAdminMenu: MenuDataItem = {
  path: '/payment',
  name: '支付管理',
  icon: <CreditCardOutlined />,
  children: [{ path: '/payment/access', name: '接入配置', icon: <KeyOutlined /> }],
};

const openPlatformMenu: MenuDataItem = {
  path: '/open-platform',
  name: '开放平台',
  icon: <ApiOutlined />,
  children: [{ path: '/open-platform/apps', name: '应用管理', icon: <ApiOutlined /> }],
};

const superAdminMenu: MenuDataItem = {
  path: '/rbac',
  name: '系统管理',
  icon: <SafetyCertificateOutlined />,
  children: [
    { path: '/system/admin-user', name: '管理员账号', icon: <IdcardOutlined /> },
    { path: '/system/role', name: '角色管理', icon: <SafetyCertificateOutlined /> },
    { path: '/system/menu', name: '菜单管理', icon: <MenuOutlined /> },
    { path: '/system/audit-log', name: '操作审计', icon: <FileSearchOutlined /> },
    { path: '/system/error-codes', name: '错误码字典', icon: <BugOutlined /> },
    { path: '/system/factory-accounts', name: '工厂账号', icon: <IdcardOutlined /> },
  ],
};

/**
 * 后端 menus 树的 icon 字段是自由文本（如 "setting" / "user"），不可靠，
 * 所以图标优先按 path 复用前端精心维护的图标；其次按 icon 名匹配；再不行用默认图标。
 */
function flattenPathIcons(
  items: MenuDataItem[],
  acc: Record<string, React.ReactNode> = {},
): Record<string, React.ReactNode> {
  for (const it of items) {
    if (it.path && it.icon) acc[it.path] = it.icon;
    if (it.children) flattenPathIcons(it.children, acc);
  }
  return acc;
}
const PATH_ICON = flattenPathIcons([
  ...menuData,
  paymentAdminMenu,
  openPlatformMenu,
  superAdminMenu,
]);

const ICON_BY_NAME: Record<string, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  catalog: <DatabaseOutlined />,
  company: <BankOutlined />,
  product: <ShopOutlined />,
  device: <LaptopOutlined />,
  firmware: <CloudUploadOutlined />,
  calendar: <CalendarOutlined />,
  sn: <BarcodeOutlined />,
  subscription: <CreditCardOutlined />,
  plan: <DollarOutlined />,
  watchface: <ClockCircleOutlined />,
  picture: <PictureOutlined />,
  llm: <RobotOutlined />,
  model: <AppstoreOutlined />,
  api: <ApiOutlined />,
  stats: <BarChartOutlined />,
  profile: <ProfileOutlined />,
  operation: <ToolOutlined />,
  notificationoutlined: <NotificationOutlined />,
  notification: <NotificationOutlined />,
  user: <TeamOutlined />,
  feedback: <CommentOutlined />,
  setting: <ToolOutlined />,
  system: <SafetyCertificateOutlined />,
  role: <SafetyCertificateOutlined />,
  menu: <MenuOutlined />,
  audit: <FileSearchOutlined />,
  // 后端各 DIRECTORY 节点下发的 icon 名
  shop: <ShopOutlined />,
  tag: <TagsOutlined />,
  crown: <CreditCardOutlined />,
  'credit-card': <CreditCardOutlined />,
  clock: <ClockCircleOutlined />,
  robot: <RobotOutlined />,
  safety: <SafetyCertificateOutlined />,
};

/**
 * 前端实际注册的导航叶子路由（与 router.tsx 对齐，已统一为后端 menus 树的 path 命名）。
 * 后端树里若出现前端尚未实现的页面（如 /cs/workbench、/system/alert 等），
 * 其叶子节点会被 buildMenuFromTree 丢弃，避免点进去 404。
 * 注意：详情页（/:id）由列表页 navigate 进入、不是菜单项，无需登记；/rbac/me 走头像下拉。
 *
 * ⚠️ **新增页面时必须往这里加一条**，否则后端菜单下发了、路由也注册了，侧边栏上
 * 依然什么都不显示，而且没有任何报错——只能靠直接敲 URL 才能访问。
 * 开发环境下 {@link warnUnknownMenuPath} 会把被丢弃的 path 打到控制台。
 */
export const KNOWN_ROUTES = new Set<string>([
  '/dashboard',
  '/catalog/company',
  '/catalog/product-hub',
  '/catalog/capabilities',
  '/catalog/fitment-target',
  '/catalog/preinstall-app',
  '/catalog/app-bundle',
  '/device/list',
  '/device/version',
  '/device/calendar',
  '/device/groups',
  '/device/ota-tasks',
  '/device/ota-baselines',
  '/device/events',
  '/device/gifts',
  '/device/slideshow-group',
  '/device/config',
  '/device/config-dispatch',
  '/sn/overview',
  '/sn/batch',
  '/sn/list',
  '/sn/test-report',
  '/sn/scrap-record',
  '/sn/factory',
  '/sn/parser',
  '/sn/scheme',
  '/subscription/plan',
  '/subscription/device-plan',
  '/subscription/device-plan/grants',
  '/subscription/esim-usage',
  '/watch-face/list',
  '/watch-face/custom',
  '/watch-face/user',
  '/ai/story-review',
  '/llm/configs',
  '/llm/models',
  '/llm/recommend',
  '/llm/stats',
  '/llm/metering',
  '/llm/prices',
  '/llm/price-audit',
  '/llm/profile',
  '/operation/user',
  '/operation/feedback',
  '/operation/partner',
  '/operation/workbench',
  '/operation/notifications/templates',
  '/operation/notifications/deliveries',
  '/repair/tickets',
  '/repair/rma',
  '/ops/alert-rules',
  '/ops/alert-events',
  '/ops/alert-channels',
  '/ops/config',
  '/ops/connectivity',
  '/ops/image-asset',
  '/system/admin-user',
  '/system/role',
  '/system/menu',
  '/system/audit-log',
  '/system/error-codes',
  '/system/factory-accounts',
  '/payment/access',
  '/open-platform/apps',
]);

/**
 * 开发环境提示：后端下发了菜单、但 path 不在 {@link KNOWN_ROUTES} 里，于是被静默丢弃。
 *
 * 这类漏登记不会报错、页面直敲 URL 又能打开，很容易一路测到上线才发现侧边栏少了入口，
 * 所以在这里吼一声。生产环境不打扰用户。
 */
const warnedPaths = new Set<string>();
function warnUnknownMenuPath(path: string, name?: string) {
  if (!import.meta.env.DEV || warnedPaths.has(path)) return;
  warnedPaths.add(path);
  console.warn(
    `[菜单] 后端下发了「${name ?? path}」(${path})，但它不在 BasicLayout 的 KNOWN_ROUTES 里，` +
      '侧边栏不会显示。若该页面已在 router.tsx 注册，请把 path 加进 KNOWN_ROUTES。',
  );
}

function resolveIcon(m: Menu): React.ReactNode {
  if (m.path && PATH_ICON[m.path]) return PATH_ICON[m.path];
  const key = m.icon?.trim().toLowerCase();
  if (key && ICON_BY_NAME[key]) return ICON_BY_NAME[key];
  return <AppstoreOutlined />;
}

/**
 * 把 /admin/rbac/me 的 menus 树转成 ProLayout 的 MenuDataItem[]。
 * - 只保留导航节点（DIRECTORY / MENU），BUTTON 节点是权限码不进导航；
 * - 过滤隐藏（visible === 0）和禁用（status === 'DISABLED'）的节点；
 * - DIRECTORY 是分组容器、其 path 多为 null，一旦子节点全被过滤就整组丢弃，
 *   绝不渲染成可点击叶子（否则点进去 404）；用 id 生成稳定 key 供 ProLayout 用；
 * - MENU/叶子节点必须有 path 且该 path 已在 router.tsx 注册、列在下方已知路由清单里才进导航，
 *   后端下发但前端尚未实现的页面直接丢弃。
 */
export function buildMenuFromTree(menus: Menu[]): MenuDataItem[] {
  return menus
    .filter((m) => m.type !== 'BUTTON' && m.visible !== 0 && m.status !== 'DISABLED')
    .map((m) => {
      const children = m.children?.length ? buildMenuFromTree(m.children) : [];
      // 后端 DIRECTORY 的 path 可能是 null，也可能是空串 ""（不同批次播种不一致）。
      // `??` 判不出空串，会让多个目录都拿到 key=""、path="" 而冲突——
      // 导致 ProLayout 无法展开这些目录、且子页选中时整组折叠。统一把空串视作"无 path"。
      const path = m.path && m.path.trim() ? m.path : undefined;
      const item: MenuDataItem = {
        key: path ?? `menu-${m.id}`,
        path,
        // 固定页面名称与静态菜单一致；存量菜单另由增量 SQL 同步。
        name: path === '/subscription/plan' ? '套餐管理'
          : path === '/subscription/device-plan/grants' ? '订阅权益赠送' : m.name,
        icon: resolveIcon(m),
      };
      if (children.length) item.children = children;
      return { item, type: m.type, hasChildren: children.length > 0 };
    })
    .filter(({ item, type, hasChildren }) => {
      if (hasChildren) return true; // 有子项的目录 → 作为分组保留
      if (type === 'DIRECTORY') return false; // 空目录丢弃
      // 叶子：必须有已注册路由
      if (!item.path) return false;
      if (KNOWN_ROUTES.has(item.path)) return true;
      warnUnknownMenuPath(item.path, item.name);
      return false;
    })
    .map(({ item }) => item);
}

/**
 * 菜单树为空且不该显示静态菜单时显示的占位项。
 * 直接渲染空侧边栏会让人以为是页面坏了，而"没有任何可用菜单"本身就是要告诉用户的信息。
 * 没有 path → 不可点击（见 menuItemRender）。
 */
const EMPTY_MENU: MenuDataItem[] = [
  { key: 'no-menu', name: '暂无可用菜单，请联系管理员', disabled: true },
];

export default function BasicLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const menus = usePermissionsStore((s) => s.menus);
  const degraded = usePermissionsStore((s) => s.degraded);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const resolvedMenu = useMemo<MenuDataItem[]>(() => {
    // 优先用后端下发的 menus 树动态渲染。
    const dynamic = buildMenuFromTree(menus);
    if (dynamic.length) {
      return placeSystemManagementLast(dynamic);
    }
    // 只有 /me 首次请求失败才使用静态菜单；接口成功但菜单为空必须如实呈现，
    // 否则会把漏执行迁移或漏授权伪装成正常状态。
    if (degraded && isSuperAdmin) {
      return placeSystemManagementLast([
        ...menuData,
        paymentAdminMenu,
        openPlatformMenu,
        superAdminMenu,
      ]);
    }
    if (degraded) return placeSystemManagementLast(menuData);
    return EMPTY_MENU;
  }, [menus, isSuperAdmin, degraded]);

  const onLogout = async () => {
    try {
      await adminLogout();
    } catch {
      // 忽略后端错误，本地登出仍然继续
    } finally {
      clear();
      navigate('/login', { replace: true });
    }
  };

  return (
    <ProLayout
      title={import.meta.env.VITE_BRAND_NAME}
      logo={null}
      layout="mix"
      splitMenus={false}
      contentWidth="Fluid"
      fixedHeader
      fixSiderbar
      location={location}
      route={{ path: '/', routes: resolvedMenu }}
      // 没有 path 的项（如 EMPTY_MENU 占位）不包 Link，否则点一下会跳回首页。
      menuItemRender={(item, dom) => (item.path ? <Link to={item.path}>{dom}</Link> : dom)}
      avatarProps={{
        src: undefined,
        size: 'small',
        title: user?.displayName || user?.username || '管理员',
        render: (_, dom) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'me',
                  icon: <KeyOutlined />,
                  label: '我的资料 / 改密',
                  onClick: () => navigate('/rbac/me'),
                },
                { type: 'divider' },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  onClick: onLogout,
                },
              ],
            }}
          >
            <span
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Avatar size="small" style={{ background: '#1e40af' }}>
                {(user?.displayName || user?.username || 'A')[0]?.toUpperCase()}
              </Avatar>
              {dom}
            </span>
          </Dropdown>
        ),
      }}
      token={{
        sider: {
          colorMenuBackground: '#0f172a',
          colorTextMenu: '#cbd5e1',
          colorTextMenuSelected: '#ffffff',
          colorBgMenuItemSelected: '#1e293b',
        },
      }}
    >
      <div style={{ padding: 24 }}>
        {/* 内层 Suspense 边界：lazy 页面切换时只在内容区显示 spinner，
            侧边栏/头部保持挂载，避免顶层 Suspense 触发整页（含侧边栏）闪一下。 */}
        <Suspense
          fallback={
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
              }}
            >
              <Spin />
            </div>
          }
        >
          {/* 错误边界放在 Suspense 里侧、包住 Outlet：页面 chunk 加载失败或渲染抛错时
              只有内容区变成错误态，侧边栏和头部还在，用户能直接换页面。 */}
          <PageErrorBoundary key={location.pathname}>
            <Outlet />
          </PageErrorBoundary>
        </Suspense>
      </div>
    </ProLayout>
  );
}
